// Which features can actually be reached, and what a switch will do.
//
// A feature can sit INSIDE another one: MY OFFERS, Verify Status and the
// profile photo are sections of the profile SCREEN, and there is no other door
// to any of them. So `offers: on` while `profile: off` is a switch that changes
// nothing — the screen already said as much ("unreachable while My Profile is
// off"), which is a caption on a state it let you create and then leave.
//
// This is the decision behind that caption, extracted so the screen can act on
// it: refuse to switch a section on alone (and offer to open its container at
// the same time), and say what goes dark before a container is switched off.
//
// The fan app resolves the same rule at
// team_app_react_native/src/utils/featureGating.js, so the app cannot show a
// section whose container is closed however the flags are set. That file's
// FEATURE_PARENTS and the `containedIn` fields here are pinned together by
// crossRepoFeatureParents.test.js.

const catalogueEntry = (catalogue, featureId) =>
    (catalogue || []).find(feature => feature.id === featureId) || null;

export function isOnForEveryone(featureId, flags) {
    const flag = flags && flags[featureId];
    return !!(flag && flag.enabled === true);
}

// How many people are explicitly set to on (or off) for a feature.
export function overrideCount(featureId, access, wanted) {
    return Object.values(access || {})
        .filter(entry => entry && entry[featureId] === wanted).length;
}

// Reachable = SOMEBODY can get to it: on for everyone, or on for at least one
// tester. A container that is off for everyone but on for one person is a real
// door for that person, so it does not block.
export function isReachable(featureId, { flags = {}, access = {} } = {}) {
    return isOnForEveryone(featureId, flags) || overrideCount(featureId, access, true) > 0;
}

/**
 * The container standing between this feature and any fan, or null when there
 * is none — either it has no container, or the container is already reachable.
 */
export function blockingParent(feature, catalogue, state = {}) {
    if (!feature || !feature.containedIn) return null;
    if (isReachable(feature.containedIn, state)) return null;
    return catalogueEntry(catalogue, feature.containedIn)
        || { id: feature.containedIn, label: feature.containedIn };
}

// Is a feature on for this one person? Their override wins either way;
// otherwise they get the Everyone switch. The fan app's resolveFeatures.
export function isOnFor(entry, featureId, flags) {
    const own = entry && entry[featureId];
    return own === true || (own !== false && isOnForEveryone(featureId, flags));
}

/**
 * Who actually gets a feature that only WORKS alongside another (`relation:
 * 'worksWith'` — the Choose Experience screen and Change Experience).
 *
 * The fan app gates it per person: it resolves on for a fan only when that
 * fan has BOTH on. So "on for everyone" with its partner on for three testers
 * is three people, not everyone — and neither Feature Access nor the chooser
 * editor may call it anything else. Both read this, so they cannot disagree.
 *
 * @returns {
 *   state:   'everyone' (both on for everyone) | 'testers' (some people have
 *            both) | 'off' (nobody has both),
 *   testers: how many people have both (state 'testers'),
 *   except:  how many people are set not to get it (state 'everyone')
 * }
 */
export function worksWithStatus(featureId, parentId, { flags = {}, access = {} } = {}) {
    const entries = Object.values(access || {}).filter(entry => entry && typeof entry === 'object');
    const getsIt = entry => isOnFor(entry, featureId, flags) && isOnFor(entry, parentId, flags);
    if (isOnForEveryone(featureId, flags) && isOnForEveryone(parentId, flags)) {
        return { state: 'everyone', testers: 0, except: entries.filter(entry => !getsIt(entry)).length };
    }
    const testers = entries.filter(getsIt).length;
    return testers > 0
        ? { state: 'testers', testers, except: 0 }
        : { state: 'off', testers: 0, except: 0 };
}

const peopleCount = (n, one = 'person', many = 'people') => `${n} ${n === 1 ? one : many}`;

/**
 * The "Right now" caption for a worksWith feature's row in Feature Access —
 * what worksWithStatus says, in words. Never a plain "On for everyone" unless
 * everyone actually gets it.
 *
 * @param feature, parent  catalogue entries
 * @param available        false when this club cannot use the feature at all
 *                         (Feature Access's own capability check) — then
 *                         nobody gets it, whatever the switches say
 */
export function worksWithSummary(feature, parent, { flags = {}, access = {}, available = true } = {}) {
    const parentName = (parent && (parent.label || parent.id)) || feature.containedIn;
    const parentId = (parent && parent.id) || feature.containedIn;
    const ownEveryone = isOnForEveryone(feature.id, flags);
    const ownOn = overrideCount(feature.id, access, true);
    const ownOff = overrideCount(feature.id, access, false);
    const base = ownEveryone
        ? (ownOff > 0 ? `On for everyone, except ${ownOff} person(s)` : 'On for everyone')
        : (ownOn > 0 ? `Hidden — on for ${ownOn} tester(s)` : 'Hidden from everyone');
    if (!ownEveryone && ownOn === 0) return base;
    if (!available) return `${base} — but nobody gets it here`;

    const status = worksWithStatus(feature.id, parentId, { flags, access });
    if (status.state === 'everyone') {
        return status.except > 0 ? `On for everyone, except ${status.except} person(s)` : 'On for everyone';
    }
    if (status.state === 'testers') {
        if (ownEveryone) {
            return `Switched on for everyone — but only ${peopleCount(status.testers)} ${status.testers === 1 ? 'gets' : 'get'} it, because ${parentName} is on for testers only`;
        }
        if (status.testers === ownOn) return base;
        return `${base} — but only ${status.testers} of them ${status.testers === 1 ? 'gets' : 'get'} it: ${parentName} is off for the rest`;
    }
    if (!isReachable(parentId, { flags, access })) return `${base} — but does nothing while ${parentName} is off`;
    return `${base} — but nobody gets it: nobody set to get it has ${parentName} on as well`;
}

/**
 * The container to offer to switch on alongside a feature being switched on
 * FOR EVERYONE, or null.
 *
 * A section drawn inside its container needs the container to be reachable
 * by somebody (blockingParent) — early access to the container is a real
 * door. A feature that only works alongside another needs its partner on for
 * everyone too: per person, the app gives it only to fans who have both, so
 * "on for everyone" beside a partner on for testers reaches those testers
 * and nobody else.
 */
export function parentToOpen(feature, catalogue, state = {}) {
    if (!feature || !feature.containedIn) return null;
    if (isDrawnInside(feature)) return blockingParent(feature, catalogue, state);
    if (isOnForEveryone(feature.containedIn, state.flags)) return null;
    return catalogueEntry(catalogue, feature.containedIn)
        || { id: feature.containedIn, label: feature.containedIn };
}

/**
 * The features inside this one that are switched on for somebody — what would
 * go dark if it were switched off, or appear if it were switched on.
 */
export function liveChildren(featureId, catalogue, state = {}) {
    return (catalogue || [])
        .filter(feature => feature.containedIn === featureId)
        .filter(feature => isReachable(feature.id, state));
}

/**
 * Every feature switched on for somebody, as { id: true }.
 *
 * Derived from the DATA rather than a catalogue, so the nav can ask "is Offers
 * on here?" without importing the screen that defines the catalogue (which
 * renders the nav — the import would be a cycle).
 */
export function liveFeatures({ flags = {}, access = {} } = {}) {
    const ids = new Set(Object.keys(flags || {}));
    Object.values(access || {}).forEach(entry =>
        Object.keys(entry || {}).forEach(id => ids.add(id)));
    const live = {};
    ids.forEach(id => {
        if (isReachable(id, { flags, access })) live[id] = true;
    });
    return live;
}

// `containedIn` is one gate with two shapes, and the sentences about it have
// to be true of both. Most sections are DRAWN inside their container — MY
// OFFERS is part of the profile screen. The Choose Experience screen is not
// drawn inside the Change Experience menu at all; it is a screen of its own
// that the fan app switches off whenever the menu is off, because a fan's
// choice means nothing while they cannot switch. A catalogue entry says which
// with `relation: 'worksWith'`; absent means drawn inside, which is every entry
// written before the chooser.
export function isDrawnInside(feature) {
    return !(feature && feature.relation === 'worksWith');
}

// "Offers is a section inside My Profile" / "Choose Experience screen only
// works alongside Change Experience" — why a feature does nothing on its own.
export function dependencyClause(feature, parent) {
    const name = (feature && (feature.label || feature.id)) || '';
    const parentName = (parent && (parent.label || parent.id)) || '';
    return isDrawnInside(feature)
        ? `${name} is a section inside ${parentName}`
        : `${name} only works alongside ${parentName}`;
}

// The verb phrase after a list of features, about their container ("it"):
// "sits inside it" while every one of them is drawn there, otherwise "only
// works alongside it", which stays true of a drawn-inside section as well.
export function dependentsClause(features) {
    const one = (features || []).length === 1;
    if ((features || []).every(isDrawnInside)) return one ? 'sits inside it' : 'sit inside it';
    return one ? 'only works alongside it' : 'only work alongside it';
}

// The same, for features already switched on when their container opens:
// "is already on inside it" / "are already on and only work alongside it".
export function alreadyOnClause(features) {
    const one = (features || []).length === 1;
    const verb = one ? 'is' : 'are';
    if ((features || []).every(isDrawnInside)) return `${verb} already on inside it`;
    return `${verb} already on and ${one ? 'only works' : 'only work'} alongside it`;
}

// "Offers", "Offers and Profile Photo", "Offers, Verify Status and Profile
// Photo" — a list an operator reads rather than a count they have to go and
// resolve themselves.
export function namesOf(features) {
    const labels = (features || []).map(feature => feature.label || feature.id);
    if (labels.length <= 1) return labels.join('');
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
